/**
 * billingApi — the typed surface over the F14 billing backend
 * (routes/billing.py, PR1/PR2 — merged, dark behind `billing_enabled`).
 *
 * Mirrors prepApi.js / coppaApi.js / guardianApi.js deliberately: every
 * billing-consuming component calls THROUGH here rather than hand-rolling
 * axios URLs, and the envelope readers (frontend/src/api/httpError.js, the
 * H4 unify #68) are the ONE place every product reads the backend's error
 * contract — see that module's header for the full envelope shape
 * (`{ error: { code, message, details?, request_id, timestamp } }`).
 *
 * ALL FOUR routes here are AUTHENTICATED (`get_current_user`), unlike the
 * webhook (public, signature-authed, never called from this app) — so every
 * call below goes through the SHARED default `axios` export, not a
 * dedicated `axios.create()` instance. That is deliberate, not an oversight:
 * it is what makes checkout-session's 426 `age_required` transparently
 * resolve through the ALREADY-INSTALLED global age/consent interceptor
 * (coppa/ageConsentInterceptor.js, installed once in App.js on this same
 * shared instance) — DOB capture + transparent retry, with zero extra code
 * here. See billingUpgradeInterceptor.js for the analogous 402 handling.
 *
 * DARK SEMANTICS (the house invisible-dark discipline): every route here
 * 404s while `billing_enabled` is off. `isBillingDark` is the ONE place that
 * checks — useBilling() (BillingContext.js) is the ONLY caller that should
 * ever call it; every other surface derives visibility from that hook, never
 * from a second ad hoc 404 check (a second checker is how "dark = invisible"
 * quietly drifts into "dark = invisible EXCEPT here").
 */
import axios from 'axios';
import { API_URL } from '../config';
import {
  friendlyError,
  readErrorCode,
  readErrorDetails,
} from '../api/httpError';

const BASE = `${API_URL}/api/v1/billing`;

// Re-exported so callers can source the canonical envelope readers from the
// billing surface (mirrors how prepApi/guardianApi re-export friendlyError).
export { friendlyError, readErrorCode, readErrorDetails };

// ── Reads ──────────────────────────────────────────────────────────────────

/**
 * The caller's OWN entitlement state (GET /billing/entitlement) — what
 * useBilling() polls once and what the 404 dark-check is read from. Response:
 *   { active, enforced, tier, status, source, current_period_end,
 *     expires_at, free_full_length_allowance, free_full_length_used }
 * 404 -> billing is dark (see isBillingDark).
 */
export async function getEntitlement(config) {
  const res = await axios.get(`${BASE}/entitlement`, config);
  return res.data;
}

/**
 * The PURCHASER's poll on a purchase they just started (GET
 * /billing/intents/{intentId}/activation) — scoped to the intent, not the
 * caller's own entitlement, so a guardian buying for a child gets the
 * CHILD's activation state instead of polling their own (unchanging) row
 * forever. Response:
 *   { intent_id, purchase_state, active, enforced, tier, status, source,
 *     current_period_end, expires_at, beneficiary_is_self, refund_pending }
 * `purchase_state` is the intent's own lifecycle (open / awaiting_payment /
 * fulfilled / expired / compensated) — `awaiting_payment` is what tells the
 * poller to say "your bank payment can take a couple of days" instead of
 * offering a retry that would just 409 again.
 * 404 -> dark, not the purchaser, or no such intent (enumeration-safe, all
 * three indistinguishable by design).
 */
export async function getPurchaseActivation(intentId, config) {
  const res = await axios.get(
    `${BASE}/intents/${encodeURIComponent(intentId)}/activation`,
    config,
  );
  return res.data;
}

// ── Writes ─────────────────────────────────────────────────────────────────

/**
 * Start a purchase (POST /billing/checkout-session) -> a Stripe Checkout
 * redirect. `beneficiaryId` omitted buys for the caller (18+ only); passed,
 * it must be a student the caller holds a VERIFIED guardian link to — the
 * SERVER decides which path applies from the caller's own age and links, a
 * client cannot elect to be guardian-routed.
 *
 * Returns `{ checkout_url, intent_id }`. THIS CALL GRANTS NOTHING — the
 * webhook is the sole writer of entitlements; the redirect is a client-
 * controlled URL and the activation page polls rather than trusting arrival
 * there. Persist `intent_id` (sessionStorage) BEFORE navigating to
 * `checkout_url` if the caller wants the activation page to resume polling
 * it after Stripe's static-path redirect back (see ActivationPage.js).
 *
 * Refusals (see billingApi's classifiers below): 400 invalid price/student,
 * 403 checkout_guardian_required, 404 dark, 409 already_entitled /
 * checkout_in_progress, 426 age_required (see the module header — resolved
 * transparently by the global age interceptor unless the DOB capture is
 * dismissed), 502/503 Stripe unreachable (safe to retry — the intent is
 * reused under the SAME idempotency key).
 */
export async function createCheckoutSession({ priceId, beneficiaryId } = {}, config) {
  const body = { price_id: priceId };
  if (beneficiaryId) body.beneficiary_id = beneficiaryId;
  const res = await axios.post(`${BASE}/checkout-session`, body, config);
  return res.data;
}

/**
 * Manage an existing billing account (POST /billing/portal-session) -> a
 * Stripe Billing Portal redirect for the CALLER's OWN Stripe customer —
 * PURCHASER-scoped by construction, so this correctly serves a guardian who
 * bought for a child (their own customer row) as well as an adult
 * self-purchaser; a student's own entitlement state carries no payment
 * controls (GET /billing/entitlement never exposes them).
 *
 * Returns `{ portal_url }`. 404 -> dark OR no billing account yet (nothing
 * to manage — the caller has never purchased); 502/503 Stripe unreachable.
 */
export async function createPortalSession(config) {
  const res = await axios.post(`${BASE}/portal-session`, {}, config);
  return res.data;
}

// ── Envelope classifiers (the "no dead ends" branch) ────────────────────────
//
// Every classifier is a PURE function of the axios error shape — safe to
// import and call unmocked in any consuming component's test (mirrors how
// coppa/coppaApi.js's isAgeRequired/isPlatformConsentRequired run for real,
// unmocked, in ExamScoreReport.test.js).

/**
 * True for a bare 404 from any billing route — the house invisible-dark
 * signal. `useBilling()` is the ONLY intended caller (see the module
 * header); every other surface reads `useBilling().enabled`, never this
 * directly, so "billing is dark" is derived from exactly one place.
 */
export function isBillingDark(err) {
  return err?.response?.status === 404;
}

/** True for the 402 `entitlement_required` upgrade-surface signal. */
export function isEntitlementRequired(err) {
  return err?.response?.status === 402 && readErrorCode(err) === 'entitlement_required';
}

/**
 * True for the 403 `checkout_guardian_required` refusal — a minor tried to
 * self-purchase, OR an adult tried to buy for a student they hold no
 * VERIFIED guardian link to. ONE code for both (non-disclosure); the
 * server's own `message`/`next_steps` already distinguish them in prose, so
 * the UI renders those verbatim rather than re-deriving which sub-case fired.
 */
export function isCheckoutGuardianRequired(err) {
  return (
    err?.response?.status === 403 &&
    readErrorCode(err) === 'checkout_guardian_required'
  );
}

/** True for the 409 `already_entitled` refusal — nothing left to buy. */
export function isAlreadyEntitled(err) {
  return err?.response?.status === 409 && readErrorCode(err) === 'already_entitled';
}

/**
 * True for the 409 `checkout_in_progress` refusal — EITHER a genuine
 * pre-charge collision (a checkout already open for this student/price) OR a
 * delayed payment settling (`awaiting_payment`, e.g. a bank debit). Both
 * share this ONE code; the server's `message` already differs correctly
 * per sub-case ("already in progress" vs. "still processing" — see
 * billing_checkout.py `_checkout_in_progress` vs. the awaiting_payment
 * branch of `open_intent`), so the UI renders the server's message +
 * next_steps verbatim instead of re-deriving the sub-case from string
 * matching.
 */
export function isCheckoutInProgress(err) {
  return (
    err?.response?.status === 409 && readErrorCode(err) === 'checkout_in_progress'
  );
}

/**
 * The bounded `next_steps` list a billing refusal ALWAYS carries (the house
 * "no dead ends" contract — CheckoutRefused/EntitlementRequired both set
 * `details.next_steps`). Returns `[]` when absent so callers can map/render
 * without a null check.
 */
export function nextSteps(err) {
  const steps = readErrorDetails(err)?.next_steps;
  return Array.isArray(steps) ? steps : [];
}

/**
 * The free-meter numbers a METERED 402 carries (`details.free_allowance` /
 * `details.free_used` — exam-start and tailored-practice refusals only). A
 * PURE-PREMIUM refusal (the study plan, or any future `enforce_premium_
 * feature` surface) carries neither field — returns `null` for both, which
 * `UpgradeDialog` reads as "render the pure-premium copy, never a meter
 * count" (see billing_entitlements.py `_upgrade_refusal`: `meter=None` omits
 * both keys entirely, it never sends a zero).
 */
export function meterFromError(err) {
  const details = readErrorDetails(err) || {};
  const { free_allowance: allowance, free_used: used } = details;
  return {
    allowance: typeof allowance === 'number' ? allowance : null,
    used: typeof used === 'number' ? used : null,
  };
}
